import React, { useState } from 'react';
import { Link, useLocation, useNavigate, Outlet } from 'react-router-dom';
import ThemeToggle from './ThemeToggle';
import { useAuth } from '../contexts/AuthContext';

interface AdminLayoutProps {
  children?: React.ReactNode;
}

const AdminLayout: React.FC<AdminLayoutProps> = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [showUserMenu, setShowUserMenu] = useState(false);

  // Safely derive initials, fallback to empty
  const initials = user
    ? `${user.firstName?.[0] || ''}${user.lastName?.[0] || ''}`
    : '';

  const isActive = (path: string) => {
    return location.pathname === path ? 'text-primary' : 'text-secondary dark:text-dark-text';
  };

  const handleLogout = () => {
    logout();
    setShowUserMenu(false);
    navigate('/login');
  };

  // Redirect to main app
  const goToMainApp = () => {
    const currentDomain = window.location.hostname;
    let mainDomain: string;

    // Handle different environments
    if (currentDomain === 'admin.avery.cloud') {
      // Local development
      mainDomain = 'riskninja.avery.cloud';
    } else if (currentDomain === 'admin-dev.riskninja.ai') {
      // AWS dev environment
      mainDomain = 'dev.riskninja.ai';
    } else if (currentDomain === 'admin.riskninja.ai') {
      // AWS production
      mainDomain = 'riskninja.ai';
    } else {
      // Fallback: just remove 'admin.' prefix
      mainDomain = currentDomain.replace('admin.', '');
    }

    window.location.href = `${window.location.protocol}//${mainDomain}`;
  };

  return (
    <div className="flex flex-col h-screen bg-slate-50 dark:bg-dark-bg">
      {/* Admin Header */}
      <header className="flex items-center justify-between whitespace-nowrap border-b border-solid border-b-[#e7eef3] dark:border-dark-border bg-gradient-to-r from-purple-600 to-indigo-600 px-10 py-3 shadow-md">
        <div className="flex items-center gap-4">
          <div className="size-8">
            <svg viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M24 4L44 12v10c0 12-8 24-20 28C12 46 4 34 4 22V12l20-8z"
                fill="white"
              />
              <path
                d="M24 16v8m0 4h.01"
                stroke="#7c3aed"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </div>
          <h2 className="text-white text-xl font-bold leading-tight tracking-[-0.015em]">
            Risk Ninja Admin Portal
          </h2>
        </div>

        <div className="flex flex-1 justify-end gap-8">
          <div className="flex items-center gap-6">
            {/* Navigation Links - Admin Only */}
            <Link
              className={`text-sm font-medium leading-normal hover:text-white transition-colors ${
                isActive('/sysadmin') ? 'text-white' : 'text-purple-100'
              }`}
              to="/sysadmin"
            >
              System Admin
            </Link>

            <Link
              className={`text-sm font-medium leading-normal hover:text-white transition-colors ${
                isActive('/sysadmin/dashboard') ? 'text-white' : 'text-purple-100'
              }`}
              to="/sysadmin/dashboard"
            >
              Audit Dashboard
            </Link>

            <button
              onClick={goToMainApp}
              className="text-sm font-medium leading-normal text-purple-100 hover:text-white transition-colors"
            >
              Back to Main App
            </button>

            {/* Theme Toggle */}
            <ThemeToggle />

            {/* User Menu */}
            <div className="relative">
              <button
                onClick={() => setShowUserMenu(!showUserMenu)}
                className="flex items-center gap-2 px-4 py-2 rounded-lg bg-white bg-opacity-20 hover:bg-opacity-30 transition-colors"
              >
                <div className="flex size-8 shrink-0 items-center justify-center rounded-full bg-white text-purple-600 font-semibold">
                  {initials}
                </div>
                <span className="text-sm font-medium text-white hidden md:inline">
                  {user?.firstName} {user?.lastName}
                </span>
                <svg
                  className={`w-4 h-4 text-white transition-transform ${showUserMenu ? 'rotate-180' : ''}`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>

              {showUserMenu && (
                <>
                  <div
                    className="fixed inset-0 z-10"
                    onClick={() => setShowUserMenu(false)}
                  />
                  <div className="absolute right-0 mt-2 w-56 rounded-lg shadow-lg bg-white dark:bg-dark-surface border border-gray-200 dark:border-dark-border z-20">
                    <div className="px-4 py-3 border-b border-gray-200 dark:border-dark-border">
                      <p className="text-sm font-medium text-gray-900 dark:text-dark-text">
                        {user?.firstName} {user?.lastName}
                      </p>
                      <p className="text-sm text-gray-500 dark:text-gray-400 truncate">
                        {user?.email}
                      </p>
                      <p className="text-xs text-purple-600 dark:text-purple-400 mt-1 font-semibold">
                        System Administrator
                      </p>
                    </div>
                    <div className="py-1">
                      <Link
                        to="/settings"
                        className="block px-4 py-2 text-sm text-gray-700 dark:text-dark-text hover:bg-gray-100 dark:hover:bg-dark-hover"
                        onClick={() => setShowUserMenu(false)}
                      >
                        Settings
                      </Link>
                      <Link
                        to="/change-password"
                        className="block px-4 py-2 text-sm text-gray-700 dark:text-dark-text hover:bg-gray-100 dark:hover:bg-dark-hover"
                        onClick={() => setShowUserMenu(false)}
                      >
                        Change Password
                      </Link>
                      <button
                        onClick={handleLogout}
                        className="block w-full text-left px-4 py-2 text-sm text-red-600 dark:text-red-400 hover:bg-gray-100 dark:hover:bg-dark-hover"
                      >
                        Logout
                      </button>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* Main Content Area */}
      <div className="flex-1 overflow-auto">
        {children || <Outlet />}
      </div>
    </div>
  );
};

export default AdminLayout;
